import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Plus, X, Upload, Image, Music, Video, FileText, 
  Link as LinkIcon, Clock, MapPin, Phone, MessageCircle, 
  Mail, Tag, ChevronDown, AlertCircle, Sparkles, HardDrive
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import AIImportWidget from '../../components/AIImportWidget';
import GoogleDrivePicker from '../../components/GoogleDrivePicker';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Chandigarh'
];

const CreateListing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [myPasses, setMyPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageInputMode, setImageInputMode] = useState('upload'); // 'upload', 'url', or 'drive'
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [showAIImport, setShowAIImport] = useState(false);
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  
  // Handle Google Drive selection
  const handleDriveSelect = (files) => {
    const maxImages = selectedTier?.max_images || 5;
    const remainingSlots = maxImages - formData.images.length;
    const filesToAdd = files.slice(0, remainingSlots).map(f => f.url);
    
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...filesToAdd]
    }));
    setShowDrivePicker(false);
    toast.success(`${filesToAdd.length} image(s) added from Google Drive`);
  };
  
  // Handle AI Import data
  const handleAIImportData = (data) => {
    setFormData(prev => ({
      ...prev,
      title: data.name || prev.title,
      description: data.description || prev.description,
      price: data.price ? String(data.price) : prev.price,
      images: data.images && data.images.length > 0 ? data.images : prev.images,
    }));
    setShowAIImport(false);
    toast.success('Form auto-filled with extracted data!');
  };
  
  const [formData, setFormData] = useState({
    listing_type: 'sell',
    title: '',
    description: '',
    category_id: '',
    subcategory_id: '',
    price: '',
    price_negotiable: false,
    condition: 'new',
    location_city: '',
    location_state: '',
    images: [],
    audio_url: '',
    video_url: '',
    pdf_url: '',
    external_url: '',
    contact_phone: '',
    contact_whatsapp: '',
    contact_email: '',
    listing_duration_hours: 0,
    custom_buttons: [],
    pass_tier: ''
  });

  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    if (!user) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [catsRes, tiersRes, passesRes] = await Promise.all([
        api.get('/p2p/categories'),
        api.get('/p2p/tiers'),
        api.get('/p2p/my-passes')
      ]);
      
      setCategories(catsRes.data);
      setTiers(tiersRes.data);
      setMyPasses(passesRes.data);
      
      // Pre-select user's available pass
      if (passesRes.data.length > 0) {
        const availablePass = passesRes.data.find(p => p.quantity > 0);
        if (availablePass) {
          setFormData(prev => ({ ...prev, pass_tier: availablePass.tier_name }));
          const tier = tiersRes.data.find(t => t.name === availablePass.tier_name);
          setSelectedTier(tier);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPassCount = (tierName) => {
    const pass = myPasses.find(p => p.tier_name === tierName);
    return pass?.quantity || 0;
  };

  const handleTierSelect = (tierName) => {
    const count = getPassCount(tierName);
    if (count === 0) {
      toast.error(`You don't have any ${tierName} passes. Buy one first.`);
      return;
    }
    setFormData({ ...formData, pass_tier: tierName });
    setSelectedTier(tiers.find(t => t.name === tierName));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!selectedTier) {
      toast.error('Please select a pass tier first');
      return;
    }
    
    const maxImages = selectedTier.max_images || 5;
    const remainingSlots = maxImages - formData.images.length;
    
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${maxImages} images allowed for ${selectedTier.name} pass`);
      return;
    }
    
    const filesToUpload = files.slice(0, remainingSlots);
    
    setUploadingImage(true);
    for (const file of filesToUpload) {
      try {
        const uploadForm = new FormData();
        uploadForm.append('file', file);
        
        const response = await api.post('/upload/image', uploadForm, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.url) {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, response.data.url]
          }));
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploadingImage(false);
  };

  const addImageByUrl = () => {
    if (!imageUrlInput.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    
    const maxImages = selectedTier?.max_images || 5;
    if (formData.images.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }
    
    // Basic URL validation
    try {
      new URL(imageUrlInput);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, imageUrlInput.trim()]
    }));
    setImageUrlInput('');
    toast.success('Image URL added!');
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pass_tier) {
      toast.error('Please select a pass tier');
      return;
    }
    
    if (!formData.title || !formData.description || !formData.category_id || !formData.price) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // Validate price against tier
    if (selectedTier && parseFloat(formData.price) > selectedTier.value_limit) {
      toast.error(`${selectedTier.name} pass allows listings up to ₹${selectedTier.value_limit.toLocaleString()}`);
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post('/p2p/create-listing', {
        ...formData,
        price: parseFloat(formData.price),
        listing_duration_hours: parseInt(formData.listing_duration_hours) || 0
      });
      
      toast.success('Listing created successfully!');
      navigate(`/deals/${response.data.listing.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const hasNoPasses = myPasses.every(p => p.quantity === 0) || myPasses.length === 0;

  if (hasNoPasses) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card p-8 text-center max-w-md">
          <Tag className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-bold mb-2">No Listing Passes</h2>
          <p className="text-muted-foreground mb-6">
            You need a listing pass to create a listing. Purchase one to get started!
          </p>
          <button
            onClick={() => navigate('/deals/buy-pass')}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-xl font-semibold"
          >
            Buy Listing Pass
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold">Create Listing</h1>
          <button
            type="button"
            onClick={() => setShowAIImport(!showAIImport)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              showAIImport 
                ? 'bg-purple-500 text-white' 
                : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            AI Import
          </button>
        </div>
        <p className="text-muted-foreground mb-4">
          Post a product to sell or request something you want to buy.
        </p>
        
        {/* AI Import Widget */}
        {showAIImport && (
          <div className="mb-6">
            <AIImportWidget 
              onDataExtracted={handleAIImportData}
              listingType="listing"
            />
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Pass Tier Selection */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" />
              Select Pass to Use
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {tiers.map(tier => {
                const count = getPassCount(tier.name);
                return (
                  <button
                    key={tier.id}
                    type="button"
                    onClick={() => handleTierSelect(tier.name)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.pass_tier === tier.name
                        ? 'border-primary bg-primary/10'
                        : count > 0
                        ? 'border-white/20 hover:border-white/40'
                        : 'border-white/10 opacity-50'
                    }`}
                  >
                    <p className="font-semibold text-sm">{tier.name}</p>
                    <p className={`text-xs ${count > 0 ? 'text-green-500' : 'text-muted-foreground'}`}>
                      {count > 0 ? `${count} available` : 'None'}
                    </p>
                  </button>
                );
              })}
            </div>
            {selectedTier && (
              <div className="mt-4 p-3 bg-white/5 rounded-xl text-sm">
                <p className="text-muted-foreground">
                  <strong className="text-foreground">{selectedTier.name}</strong>: 
                  Up to ₹{selectedTier.value_limit?.toLocaleString()} • 
                  {selectedTier.max_images} images • 
                  {selectedTier.allow_video ? 'Video ✓' : ''} 
                  {selectedTier.allow_timer ? '• Timer ✓' : ''}
                </p>
              </div>
            )}
          </div>

          {/* Listing Type */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4">What do you want to do?</h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, listing_type: 'sell' })}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.listing_type === 'sell'
                    ? 'border-green-500 bg-green-500/10'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <Package className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="font-semibold">I'm Selling</p>
                <p className="text-xs text-muted-foreground">Post a product/service</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, listing_type: 'buy' })}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.listing_type === 'buy'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <Package className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="font-semibold">I'm Buying</p>
                <p className="text-xs text-muted-foreground">Post a requirement</p>
              </button>
            </div>
          </div>

          {/* Basic Info */}
          <div className="glass-card p-6 space-y-4">
            <h3 className="font-semibold mb-4">Basic Information</h3>
            
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., iPhone 15 Pro Max 256GB"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your item in detail..."
                rows={4}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value, subcategory_id: '' })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Condition</label>
                <select
                  value={formData.condition}
                  onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                >
                  <option value="new">Brand New</option>
                  <option value="like_new">Like New</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="used">Used</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Price (₹) *</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="Enter price"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  required
                />
                {selectedTier && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Max: ₹{selectedTier.value_limit?.toLocaleString()} for {selectedTier.name}
                  </p>
                )}
              </div>
              
              <div className="flex items-center pt-8">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.price_negotiable}
                    onChange={(e) => setFormData({ ...formData, price_negotiable: e.target.checked })}
                    className="w-5 h-5 rounded"
                  />
                  <span>Price is Negotiable</span>
                </label>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Image className="w-5 h-5 text-primary" />
                Images ({formData.images.length}/{selectedTier?.max_images || 5})
              </h3>
              
              {/* Toggle between Upload, URL, and Drive */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setImageInputMode('upload')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                    imageInputMode === 'upload' 
                      ? 'bg-primary text-black' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <Upload className="w-4 h-4 inline mr-1" />
                  Upload
                </button>
                <button
                  type="button"
                  onClick={() => setImageInputMode('url')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                    imageInputMode === 'url' 
                      ? 'bg-primary text-black' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <LinkIcon className="w-4 h-4 inline mr-1" />
                  URL
                </button>
                <button
                  type="button"
                  onClick={() => setShowDrivePicker(true)}
                  className="px-3 py-1 rounded-lg text-sm font-medium transition-all bg-green-500/20 text-green-400 hover:bg-green-500/30"
                >
                  <HardDrive className="w-4 h-4 inline mr-1" />
                  Drive
                </button>
              </div>
            </div>
            
            {/* URL Input Mode */}
            {imageInputMode === 'url' && formData.images.length < (selectedTier?.max_images || 5) && (
              <div className="flex gap-2 mb-4">
                <input
                  type="url"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  placeholder="Paste image URL here..."
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:outline-none"
                />
                <button
                  type="button"
                  onClick={addImageByUrl}
                  className="px-4 py-3 bg-primary text-black rounded-xl font-medium hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            )}
            
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {formData.images.map((img, index) => (
                <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-white/5">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {imageInputMode === 'upload' && formData.images.length < (selectedTier?.max_images || 5) && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="aspect-square rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-2 hover:border-primary transition-colors"
                >
                  {uploadingImage ? (
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Plus className="w-6 h-6" />
                      <span className="text-xs">Add Image</span>
                    </>
                  )}
                </button>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* Location */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Location
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">City *</label>
                <input
                  type="text"
                  value={formData.location_city}
                  onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                  placeholder="Enter city"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">State *</label>
                <select
                  value={formData.location_state}
                  onChange={(e) => setFormData({ ...formData, location_state: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  required
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              Contact Information
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Contact info is visible only to activated members.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+91 XXXXX XXXXX"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">WhatsApp</label>
                <input
                  type="tel"
                  value={formData.contact_whatsapp}
                  onChange={(e) => setFormData({ ...formData, contact_whatsapp: e.target.value })}
                  placeholder="+91 XXXXX XXXXX"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Timer (if allowed) */}
          {selectedTier?.allow_timer && (
            <div className="glass-card p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Listing Timer (Optional)
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Set an expiry time for your listing. Leave at 0 for no expiry.
              </p>
              
              <div className="grid grid-cols-4 gap-3">
                {[0, 24, 72, 168, 720].map(hours => (
                  <button
                    key={hours}
                    type="button"
                    onClick={() => setFormData({ ...formData, listing_duration_hours: hours })}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.listing_duration_hours === hours
                        ? 'border-primary bg-primary/10'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <p className="font-semibold text-sm">
                      {hours === 0 ? 'No Limit' : hours < 24 ? `${hours}h` : `${hours / 24}d`}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-primary-foreground py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 transition-all disabled:opacity-50"
          >
            {submitting ? (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                Creating Listing...
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                Create Listing
              </>
            )}
          </button>
        </form>
      </div>
      
      {/* Google Drive Picker Modal */}
      {showDrivePicker && (
        <GoogleDrivePicker
          onSelect={handleDriveSelect}
          onClose={() => setShowDrivePicker(false)}
          imagesOnly={true}
        />
      )}
    </div>
  );
};

export default CreateListing;
